import React, { FunctionComponent, useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { IConnection } from '../interfaces/models/connection'
import ProfilePhoto from '../components/ProfilePhoto'
import Text from '../components/Text'
import Row from '../components/Row'
import Button from '../components/Button'
import Column from '../components/Column'
import moment from 'moment'
import ConnectionActionDropdown from './dropdowns/ConnectionActionDropdown'
import ApplyRemoveTagForm from './forms/ApplyRemoveTagForm'
import ActionText from '../components/ActionText'
import TooltipWrapper from '../components/TooltipWrapper'
import { parseLinksInText } from '../utils'
import { styles } from '../styles/themes/style'
import EditDeleteActionDropdown from './dropdowns/EditDeleteActionDropdown'
import { useAppHandlers } from '../services/actions/app/useAppHandlers'
import CreateEditNoteForm from './forms/CreateEditNoteForm'
import { useTasksHandlers } from '../services/actions/tasks/useTasksHandlers'
import { ITaskResponse } from '../interfaces/response/task'
import CreateEditTaskForm from './forms/CreateEditTaskForm'
import { useAuthStore } from '../stores/auth/useAuthStore'
import { useConnectionsStore } from '../stores/connections/useConnectionsStore'
import { useAppStore } from '../stores/application/useAppStore'
import Checkbox from '../components/Checkbox'
import { AppRoutes, SidebarTypes } from '../interfaces/store/appStore'
import { useNavigate } from 'react-router-dom'
import SvgFragment from './SvgFragment'
import { useConnectionsHandlers } from '../services/actions/connections/useConnectionsHandlers'
import { useWorkspaceHandlers } from '../services/actions/workspace/useWorkspaceHandlers'
import { useConnectionsActions } from '../stores/connections/useConnectionsActions'
import LoadingSpinner from '../components/LoadingSpinner'
import { EnrichmentType } from '../pages/canvas/components/connections/EnrichmentTypeEnum'
import { isLNFirst } from '../utils'

interface IConnectionSidebarFragmentProps {
    connection: IConnection,
    onChangedTagsHandler?: (updatedConnection: IConnection) => void
    onChangedNotesHandler?: (updatedConnection: IConnection) => void
    conversationTags?: any[]
    onConversationApplyRemoveTags?: (connectionId: string, tagsToAdd: string[], tagsToRemove: string[]) => Promise<any>
    inboxSurface?: boolean
    onInboxConnectionResolvedForEnrich?: (connection: IConnection | null) => void
 }

const ConnectionSidebarFragment: FunctionComponent<IConnectionSidebarFragmentProps> = ({connection, onChangedTagsHandler, onChangedNotesHandler, conversationTags, onConversationApplyRemoveTags, inboxSurface = false, onInboxConnectionResolvedForEnrich}) => {

    const [currentConnection, setCurrentConnection] = useState<IConnection>(connection)
    const { getConnectionTasks, createTaskHandler, removeTaskHandler, updateTaskHandler } = useTasksHandlers()
    const { setDestructiveModal, hideModal, closeSidebar, showErrorToast, showSuccessToast } = useAppHandlers()
    const { store: { workspace, workspace: { showAutoTags }, user, isLoggedToLinkedin }} = useAuthStore()
    const { store: { connections } } = useConnectionsStore()
    const { store: { extensionInstalled, extensionVersion } } = useAppStore()
    const { estimateEnrichmentCreditsHandler, enrichConnectionsHandler, getEnrichedTransactionByIdHandler, getEnrichedConnectionsHandler, visitConnectionAndUpdateHandler, messageClickHandler, resolveLinkedinProfileForInboxEnrichHandler } = useConnectionsHandlers()
    const navigate = useNavigate();
    const { refetchWorkspace } = useWorkspaceHandlers();

    const [connectionProperties, setConnectionProperties] = useState<boolean>(false)
    const [showEnrichProperties, setShowEnrichProperties] = useState<boolean>(true)
    const [addTask, setAddTask] = React.useState(false);
    const [addingTask, setAddingTask] = React.useState(false);
    const [tasks, setTasks ] = useState<ITaskResponse[]>([])
    const [editingTask, setEditingTask] = useState<any>();
    const [privateEmailChecked, setPrivateEmailChecked] = useState(false);
    const [businessEmailChecked, setBusinessEmailChecked] = useState(false);
    const [phoneChecked, setPhoneChecked] = useState(false);
    const [allDataEnriched, setAllDataEnriched] = useState(false);
    const [phoneEnriched, setPhoneEnriched] = useState(false);
    const [businessEmailEnriched, setBusinessEmailEnriched] = useState(false);
    const [privateEmailEnriched, setPrivateEmailEnriched] = useState(false);
    const [enrichmentInProgress, setEnrichmentInProgress] = useState(false);
    const [enrichingField, setEnrichingField] = useState<'businessEmail' | 'privateEmail' | 'phone' | null>(null);

    const { setEnrichingConnections } = useConnectionsActions();

    type EnrichFlags = { privateEmailChecked: boolean; businessEmailChecked: boolean; phoneChecked: boolean };
    const isExtensionVersionCorrect = extensionVersion === process.env.REACT_APP_VERSION
    const canMessageConnection =
      !workspace?.isStarter &&
      isLoggedToLinkedin &&
      extensionInstalled &&
      isExtensionVersionCorrect &&
      !!currentConnection?.publicIdentifier &&
      isLNFirst(currentConnection, user?._id)

    const messageTooltip =
        canMessageConnection
            ? undefined
            : !extensionInstalled
                ? 'Download extension'
                : 'Contact must be LinkedIn 1st'

    const submitConnectionsTagsHandler = useCallback((newConnection: IConnection) => {
        if(newConnection) setCurrentConnection({ ...newConnection, ...!showAutoTags ? { tags: newConnection.tags?.filter(tag => !tag.isForbidden) } : {}})
        // hide Cancel/Save buttons if last tag is removed
        if(newConnection.tags?.filter(tag => !tag.isForbidden)?.length === 0){
            // setAddingTag(false); // This line is removed as per the edit hint
        }
        hideModal()

        onChangedTagsHandler?.(newConnection);
    }, [currentConnection])

    const onCreatedNoteHandler = (updatedConnection: IConnection) => {
        onChangedNotesHandler?.(updatedConnection);
    }

    const normalizeConnectionForDisplay = useCallback((conn: IConnection): IConnection => ({
        ...conn,
        ...(!showAutoTags && conn.tags
            ? { tags: conn.tags.filter(tag => !tag.isForbidden) }
            : {}),
    }), [showAutoTags])

    const applyConnectionUpdate = useCallback((updated: IConnection) => {
        const normalized = normalizeConnectionForDisplay(updated)
        setCurrentConnection(normalized)
        if (inboxSurface) {
            onInboxConnectionResolvedForEnrich?.(normalized)
        }
    }, [inboxSurface, normalizeConnectionForDisplay, onInboxConnectionResolvedForEnrich])

    useEffect(() => {
        setCurrentConnection(normalizeConnectionForDisplay(connection))
    }, [connection._id, connection.profilePicture, connection.headline, connection.firstName, connection.lastName, connection.jobTitle, connection.company, connection.location, connection.email, connection.phoneNumber, connection.followerCount, normalizeConnectionForDisplay])

    useEffect(() => {
        // Load tasks only when connection ID changes
        loadTasksHandler();
    }, [currentConnection._id])

    useEffect(() => {
        const phoneEnriched = (currentConnection.enrichedPhones && currentConnection.enrichedPhones.length > 0) || false
        const businessEmailEnriched = (currentConnection.enrichedBusinessEmails && currentConnection.enrichedBusinessEmails.length > 0) || false
        const privateEmailEnriched = (currentConnection.enrichedPrivateEmails && currentConnection.enrichedPrivateEmails.length > 0) || false
        setAllDataEnriched(phoneEnriched && businessEmailEnriched && privateEmailEnriched);
        setPhoneEnriched(phoneEnriched)
        setBusinessEmailEnriched(businessEmailEnriched)
        setPrivateEmailEnriched(privateEmailEnriched)
        setPhoneChecked(false)
        setBusinessEmailChecked(false)
        setPrivateEmailChecked(false)
    }, [currentConnection.enrichedPhones, currentConnection.enrichedBusinessEmails, currentConnection.enrichedPrivateEmails])

    useEffect(() => {
        const updatedConnection = connections.find((c: IConnection) => c._id === connection._id);
        if (!updatedConnection) return

        const hasProfileUpdates =
            updatedConnection.profilePicture !== currentConnection.profilePicture ||
            updatedConnection.headline !== currentConnection.headline ||
            updatedConnection.firstName !== currentConnection.firstName ||
            updatedConnection.lastName !== currentConnection.lastName ||
            updatedConnection.jobTitle !== currentConnection.jobTitle ||
            updatedConnection.company !== currentConnection.company ||
            updatedConnection.location !== currentConnection.location ||
            updatedConnection.email !== currentConnection.email ||
            updatedConnection.phoneNumber !== currentConnection.phoneNumber ||
            updatedConnection.followerCount !== currentConnection.followerCount

        const hasEnrichedDataUpdates = (updatedConnection?.enrichedPrivateEmails && updatedConnection?.enrichedPrivateEmails.length > 0)
            || (updatedConnection?.enrichedBusinessEmails && updatedConnection?.enrichedBusinessEmails.length > 0)
            || (updatedConnection?.enrichedPhones && updatedConnection?.enrichedPhones.length > 0);

        const hasTagsUpdates = JSON.stringify(updatedConnection?.tags) !== JSON.stringify(currentConnection?.tags);

        if (hasProfileUpdates || hasEnrichedDataUpdates || hasTagsUpdates) {
            setCurrentConnection(normalizeConnectionForDisplay({
                ...currentConnection,
                ...updatedConnection,
            }))
        }
    }, [connections.find((c: IConnection) => c._id === connection._id)]);
 
    useEffect(() => {
        if(!showAutoTags && currentConnection.tags) {
            setCurrentConnection({...currentConnection, tags: currentConnection.tags.filter(tag => !tag.isForbidden)})
        }
    }, [showAutoTags])

    const loadTasksHandler = useCallback(async () => {
            let tasks = await getConnectionTasks(currentConnection._id);
            setTasks(tasks);
    }, []);

    const onCloseTaskFormHandler = async () => {
        setAddTask(false); 
        setEditingTask(undefined);
    }

    const onCreateUpdateTaskHandler = async (taskId: string | null, task: any) => {
        setAddTask(false); 
        setAddingTask(true);
        
        if (taskId) {
            // Optimistic update for existing task
            setTasks(prevTasks => prevTasks.map(t => t._id === taskId ? { ...t, ...task } : t));
            
            const result = await updateTaskHandler(taskId, task);
            if (result) {
                // Update local state with the actual server response
                setTasks(prevTasks => prevTasks.map(t => t._id === taskId ? result : t));
            } else {
                // Rollback on error
                loadTasksHandler();
            }
        } else {
            // Optimistic update: Create temporary task with optimistic ID
            const tempId = `temp-${Date.now()}`;
            const optimisticTask = {
                ...task,
                _id: tempId,
                createdAt: new Date().toISOString(),
            };
            
            // Check if the task should be shown for this connection
            const hasConnection = task.associations?.connections?.some((conn: any) => {
                const connId = typeof conn === 'string' ? conn : (conn._id || conn);
                return connId === currentConnection._id;
            });
            
            if (hasConnection) {
                // Add optimistic task immediately
                setTasks(prevTasks => [...prevTasks, optimisticTask]);
            }
            
            // Create task via API
            const result = await createTaskHandler(task);
            
            if (result?.success && (result?.task || result?.response || result?.data)) {
                const newTask = result?.task || result?.response || result?.data;
                // Replace optimistic task with real task from server
                setTasks(prevTasks => prevTasks.map(t => t._id === tempId ? newTask : t));
            } else {
                // Remove optimistic task on error
                setTasks(prevTasks => prevTasks.filter(t => t._id !== tempId));
            }
        }
        
        setAddingTask(false); 
        setEditingTask(undefined);
    }

    const onDeleteTaskHandler = async (taskId: string) => {
        setDestructiveModal({
            headingText: "Delete task",
            descriptionText: "Are you sure you want to delete this task?",
            onCancelHandler: () => hideModal(),
            onConfirmHandler: async () => {
                hideModal()
                deleteTaskConfirmed(taskId)
            }
        })
    }

    const deleteTaskConfirmed = async (taskId: string) => {
        // Optimistic update: remove from local state immediately
        setTasks(prevTasks => prevTasks.filter(t => t._id !== taskId));
        
        // Call API and update global store
        const result = await removeTaskHandler(taskId);
        
        // Rollback on error
        if (!result?.success) {
            // Reload tasks on failure to restore correct state
            loadTasksHandler();
        }
    }

    const onEditTaskHandler = async (task: any) => {
        setEditingTask(task);
        setAddTask(true);
    }

    const runEnrichment = async (flags: EnrichFlags, singleField?: 'businessEmail' | 'privateEmail' | 'phone') => {
        setEnrichmentInProgress(true);

        let conn = currentConnection
        if (inboxSurface) {
            try {
                conn = await resolveLinkedinProfileForInboxEnrichHandler(currentConnection)
                setCurrentConnection(conn)
                onInboxConnectionResolvedForEnrich?.(conn)
            } catch {
                conn = currentConnection
            }
        }

        // Keep table in sync (show spinners / disable other enrichment actions) for both:
        // - single-field enrich again
        // - multi-field enrich (the main "Enrich" button)
        const itemsToEnrich: Array<{ connectionId: string; type: EnrichmentType }> = [];
        if (flags.privateEmailChecked) itemsToEnrich.push({ connectionId: conn._id, type: EnrichmentType.PRIVATE_EMAIL });
        if (flags.businessEmailChecked) itemsToEnrich.push({ connectionId: conn._id, type: EnrichmentType.BUSINESS_EMAIL });
        if (flags.phoneChecked) itemsToEnrich.push({ connectionId: conn._id, type: EnrichmentType.PHONE });
        if (itemsToEnrich.length > 0) {
            setEnrichingConnections(itemsToEnrich);
        }

        if (singleField) {
            setEnrichingField(singleField);
        } else {
            setEnrichingField(null);
        }

        let enrichedTransactionId: string | undefined;

        // first, estimate credits
        const resultEstimate = await estimateEnrichmentCreditsHandler({
            connectionIds: [conn._id],
            csvRows: undefined,
            privateEmailChecked: flags.privateEmailChecked,
            businessEmailChecked: flags.businessEmailChecked,
            phoneChecked: flags.phoneChecked,
        });
        if (resultEstimate?.enrichedTransactionId) {
            enrichedTransactionId = resultEstimate?.enrichedTransactionId;
        }
        // then, enrich
        const resultEnrich = await enrichConnectionsHandler({
            enrichedTransactionId
        });

        if (resultEnrich?.enrichedTransactionId) {
            pollEnrichedTransactionForConnectionsById(resultEnrich.enrichedTransactionId, flags);
        } else {
            setEnrichingField(null);
            setEnrichingConnections([]);
            setEnrichmentInProgress(false);
        }
    };

    const onClickEnrichHandler = async () => {
        await runEnrichment({ privateEmailChecked, businessEmailChecked, phoneChecked });
    }

    const pollEnrichedTransactionForConnectionsById = async (
        enrichedTransactionId: string,
        flags: EnrichFlags
      ) => {
        const intervalId = setInterval(async () => {
          const result = await getEnrichedTransactionByIdHandler({
            enrichedTransactionId,
          });
          if (result?.failed) {
            clearInterval(intervalId);
            showErrorToast({ message: "Enrichment failed." });
            setEnrichingField(null);
            setEnrichingConnections([]);
            setEnrichmentInProgress(false);
          } else if (result?.enriched) {
            
            clearInterval(intervalId);

            let hasPrivate = !!result?.privateEmailsCountAfterEnrichment;
            let hasBusiness = !!result?.businessEmailsCountAfterEnrichment;
            let hasPhone = !!result?.phonesCountAfterEnrichment;
            if (!hasPrivate && !hasBusiness && !hasPhone) {
              const notFound = [];
              if (flags.privateEmailChecked) notFound.push("personal email");
              if (flags.businessEmailChecked) notFound.push("business email");
              if (flags.phoneChecked) notFound.push("phone number");
              let message = "No ";
              if (notFound.length === 1) {
                message += `${notFound[0]} found.`;
              } else if (notFound.length === 2) {
                message += `${notFound[0]} or ${notFound[1]} found.`;
              } else if (notFound.length === 3) {
                message += `${notFound[0]}, ${notFound[1]} or ${notFound[2]} found.`;
              } else {
                message += "enrichment data found.";
              }
              showErrorToast({ message });
            } else {
              let message = `1 contact successfully enriched`;
              const details: string[] = [];
              if (hasPrivate) {
                details.push(
                  `${result?.privateEmailsCountAfterEnrichment} personal email`
                );
              }
              if (hasBusiness) {
                details.push(
                  `${result?.businessEmailsCountAfterEnrichment} business email`
                );
              }
              if (hasPhone) {
                details.push(`${result?.phonesCountAfterEnrichment} phone`);
              }
              if (details.length > 0) {
                message += ` (${details.join(", ")}).`;
              }
              showSuccessToast({
                message: message,
              });
            }

            setEnrichingField(null);
            setEnrichingConnections([]);
            setEnrichmentInProgress(false);
            
            // update connections
            getEnrichedConnections(result?._id);
            refetchWorkspace();
          }
        }, 8000);
    };

    const onClickEnrichAgainField = async (field: 'businessEmail' | 'privateEmail' | 'phone') => {
        if (enrichmentInProgress) return;
        if (!workspace?.enrichmentCredits || workspace.enrichmentCredits <= 0) return;

        const flags: EnrichFlags =
            field === 'businessEmail'
                ? { privateEmailChecked: false, businessEmailChecked: true, phoneChecked: false }
                : field === 'privateEmail'
                    ? { privateEmailChecked: true, businessEmailChecked: false, phoneChecked: false }
                    : { privateEmailChecked: false, businessEmailChecked: false, phoneChecked: true };

        await runEnrichment(flags, field);
    };

    const getEnrichedConnections = async (enrichedTransactionId: string) => {
        const res: any = await getEnrichedConnectionsHandler({
            connectionIds: [currentConnection._id],
            enrichedTransactionId,
        });
        // In Inbox surface, the connection may not exist in the Connections store list,
        // so rely on the API response to update the local sidebar state.
        const updated = Array.isArray(res?.connections)
            ? res.connections.find((c: any) => c?._id === currentConnection._id)
            : null;
        if (updated) {
            setCurrentConnection(prev => ({
                ...prev,
                enrichedPrivateEmails: updated.enrichedPrivateEmails,
                enrichedBusinessEmails: updated.enrichedBusinessEmails,
                enrichedPhones: updated.enrichedPhones,
            }));
        }
    };

    const onMessageHandler = useCallback(async (e: React.MouseEvent<HTMLElement>) => {
        if(!canMessageConnection) return;
        await messageClickHandler(e, currentConnection);
    }, [canMessageConnection, currentConnection, messageClickHandler]);

    const SimplePropertyRow: React.FC<{label: string; value?: string;}> = ({ label, value }) => {
        return (
            <Column>
                <Text $label $black>{label}</Text>
                <Text $black>{value || 'Not disclosed'}</Text>
            </Column>
        );
    };

    const renderEnrichedInfoRow = (args: {label: string; value?: string; iconType?: string; mailIcon?: boolean; onEnrichAgain?: () => void; enrichAgainDisabled?: boolean; isEnriching?: boolean;}) => {
        const { label, value, iconType = 'checkGreen', mailIcon = false, onEnrichAgain, enrichAgainDisabled, isEnriching } = args;
        const actionsDisabled = Boolean(isEnriching);
        const handleCopy = () => {
            if (value) {
            navigator.clipboard.writeText(value)
                .then(() => {
                    showSuccessToast({message: `${value} is copied to clipboard`})
                    console.log('Copied to clipboard:', value);
                })
                .catch((err) => {
                    console.error('Failed to copy:', err);
                });
            }
        };

        return (
            <EnrichRow svgContentHover gap="8px">
                <Column style={{ alignSelf: 'flex-start', marginTop: 2 }}>
                    {isEnriching ? (
                        <LoadingSpinner width="16px" height="16px" />
                    ) : (
                        <SvgFragment type={iconType}/>
                    )}
                </Column>
                <EnrichColumn>
                    <EnrichRow style={{ justifyContent: 'space-between', height: '20px' }}>
                        <Text $label $black>{label}</Text>
                        <SvgContent gap="12px">
                            {onEnrichAgain && (
                                <TooltipWrapper tooltipText="Enrich again">
                                    <div
                                        onClick={() => {
                                            if (enrichAgainDisabled || actionsDisabled) return;
                                            onEnrichAgain();
                                        }}
                                        style={{
                                            opacity: (enrichAgainDisabled || actionsDisabled) ? 0.5 : 1,
                                            cursor: (enrichAgainDisabled || actionsDisabled) ? 'not-allowed' : 'pointer',
                                            pointerEvents: (enrichAgainDisabled || actionsDisabled) ? 'none' : 'auto'
                                        }}
                                    >
                                        <EnrichAgainSvgFragment type='reload'/>
                                    </div>
                                </TooltipWrapper>
                            )}
                            {mailIcon && (
                                <TooltipWrapper tooltipText="Email">
                                    <div
                                        onClick={() => {
                                            if (actionsDisabled) return;
                                            window.open(`mailto:${value}`);
                                        }}
                                        style={{
                                            opacity: actionsDisabled ? 0.5 : 1,
                                            cursor: actionsDisabled ? 'not-allowed' : 'pointer',
                                            pointerEvents: actionsDisabled ? 'none' : 'auto'
                                        }}
                                    >
                                        <StyledSvgFragment type='mail'/>
                                    </div>
                                </TooltipWrapper>
                            )}
                            <TooltipWrapper tooltipText="Copy">
                                <div
                                    onClick={() => {
                                        if (actionsDisabled) return;
                                        handleCopy();
                                    }}
                                    style={{
                                        opacity: actionsDisabled ? 0.5 : 1,
                                        cursor: actionsDisabled ? 'not-allowed' : 'pointer',
                                        pointerEvents: actionsDisabled ? 'none' : 'auto'
                                    }}
                                >
                                    <StyledSvgFragment type='copy'/>
                                </div>
                            </TooltipWrapper>
                        </SvgContent>
                    </EnrichRow>
                    <Text $black>{value}</Text>
                </EnrichColumn>
            </EnrichRow>
        );
    };

    const renderEnrichmentSectionItem = (args: {
        enriched: boolean;
        checked: boolean;
        setChecked: (value: boolean) => void;
        checkboxId: string;
        label: string;
        enrichedValue?: string;
        checkboxLabel: string;
        mailIcon?: boolean;
        field?: 'businessEmail' | 'privateEmail' | 'phone';
    }) => {
        const { enriched, checked, setChecked, checkboxId, label, enrichedValue, checkboxLabel, mailIcon = false, field } = args;

        if (!enriched) {
            return (
                <Row gap="10px" alignItems style={{ height: "20px" }}>
                    <Checkbox
                        checked={checked}
                        checkboxId={checkboxId}
                        onClickHandler={() => setChecked(!checked)}
                    />
                    <Text $bold $lighter>{checkboxLabel}</Text>
                </Row>
            );
        }

        const enrichAgainDisabled = Boolean(enrichmentInProgress) && Boolean(field) && enrichingField !== field;
        return renderEnrichedInfoRow({
            label,
            value: enrichedValue || '',
            mailIcon,
            onEnrichAgain: field ? () => onClickEnrichAgainField(field) : undefined,
            enrichAgainDisabled,
            isEnriching: !!field && enrichingField === field,
        });
    };

    return <Container>
        <StylesProfilePhoto
            source={currentConnection.profilePicture}
            capitalLetters={
                [currentConnection.firstName?.[0], currentConnection.lastName?.[0]].filter(Boolean).join('') || 'NN'
            }
            width="64px"
            height="64px"
            minWidth="64px"
            minHeight="64px"
        />
        <section>
            <Text $bold $black $heading4>{`${currentConnection.firstName} ${currentConnection.lastName}`}</Text>
            <Text $black>{currentConnection.headline}</Text>
        </section>
        <UpdateVisitProfileSection gap="10px">
            {!inboxSurface && (
                <Button
                    $type="blue"
                    $customWidth={true}
                    onClickHandler={onMessageHandler}
                    disabled={!canMessageConnection}
                    tooltipMessage={messageTooltip}
                >
                    {'Message'}
                </Button>
            )}
            <SidebarOutlineButton
                $type="white"
                $mediumBigButton
                onClickHandler={() => visitConnectionAndUpdateHandler(currentConnection)}
                disabled={!currentConnection.publicIdentifier}
            >
                {'Visit profile'}
            </SidebarOutlineButton>
            <MoreOutlineWrapper>
                <ConnectionActionDropdown
                    connection={currentConnection}
                    hidePrimaryActions
                    backgroundColor={styles.colors.white}
                    onConnectionUpdated={applyConnectionUpdate}
                />
            </MoreOutlineWrapper>
        </UpdateVisitProfileSection>
            <Column gap='16px'>
                <div
                    style={{
                      gap: "10px",
                      display: "flex",
                      justifyContent: "left",
                      flexDirection: "column",
                      alignItems: "left",
                      backgroundColor: inboxSurface ? styles.colors.white : styles.colors.black100,
                      borderRadius: "4px",
                    }}
                  >
                <Button $spaceBetween $type={inboxSurface ? 'white' : 'black100'} onClickHandler={() => setShowEnrichProperties(!showEnrichProperties)}>
                    <div>{allDataEnriched ? "Enriched info" : "Enrich contact"}</div>
                    <StyledArrowSvg 
                        dropdownOpen={showEnrichProperties} 
                        xmlns="http://www.w3.org/2000/svg" 
                        width="12" 
                        height="12" 
                        viewBox="0 0 12 7"
                    >
                        <path fillRule="evenodd" d="M6.193 14.215l.623.6c.257.246.674.247.932 0L12 10.747l4.252 4.068c.258.247.675.247.932 0l.623-.6c.257-.247.257-.648 0-.896l-5.34-5.133c-.258-.248-.676-.248-.933 0l-5.34 5.133c-.258.247-.258.649 0 .896z" transform="translate(-209 -182) translate(30 120) translate(11 54) translate(162)"/>
                    </StyledArrowSvg>
                </Button>
                {showEnrichProperties &&
                    <div  
                        style={{
                            padding: "0px 8px 8px 8px",
                        }}>
                            <Column gap='8px'>
                                {renderEnrichmentSectionItem({
                                    enriched: businessEmailEnriched,
                                    checked: businessEmailChecked,
                                    setChecked: setBusinessEmailChecked,
                                    checkboxId: "businessEmailChecked",
                                    label: "Business email",
                                    checkboxLabel: "Find business email",
                                    enrichedValue: currentConnection.enrichedBusinessEmails?.[0]?.enrichedValue,
                                    mailIcon: true,
                                    field: "businessEmail"
                                })}

                                {renderEnrichmentSectionItem({
                                    enriched: privateEmailEnriched,
                                    checked: privateEmailChecked,
                                    setChecked: setPrivateEmailChecked,
                                    checkboxId: "privateEmailChecked",
                                    label: "Personal email",
                                    checkboxLabel: "Find personal email",
                                    enrichedValue: currentConnection.enrichedPrivateEmails?.[0]?.enrichedValue,
                                    mailIcon: true,
                                    field: "privateEmail"
                                })}

                                {renderEnrichmentSectionItem({
                                    enriched: phoneEnriched,
                                    checked: phoneChecked,
                                    setChecked: setPhoneChecked,
                                    checkboxId: "phoneChecked",
                                    label: "Phone number",
                                    checkboxLabel: "Find phone number",
                                    enrichedValue: currentConnection.enrichedPhones?.[0]?.enrichedValue,
                                    field: "phone"
                                })}
                                {!allDataEnriched &&
                                <>
                                    <Row gap="10px" style={{ marginTop: "8px", marginBottom: "8px" }}>
                                        {workspace.enrichmentCredits > 0 ?
                                            <Button 
                                                    $bigButton 
                                                    $type='blue'
                                                    $customWidth={true}
                                                    $SVGtype="enrichDisabled"
                                                    $customDisabled={styles.colors.primary400}
                                                    disabled={!phoneChecked && !businessEmailChecked && !privateEmailChecked}
                                                    isLoading={enrichmentInProgress}
                                                    loadingText={'Enrich'}
                                                    onClickHandler={onClickEnrichHandler}>{'Enrich'}</Button>
                                            :
                                            <Button 
                                                $bigButton $type='blue' 
                                                onClickHandler={() => {navigate(AppRoutes.SETTINGS_BILLING); closeSidebar(SidebarTypes.CONNECTION_ACTIONS)}}>{'Buy credits'}</Button>
                                        }
                                    </Row>
                                    <EnrichmentCredits gap='5px' alignItems>
                                        <SvgFragment type='coins'/>Credits left: <span style={{fontWeight: 'bold'}}>{workspace.enrichmentCredits}</span> 
                                        <InfoMenuSvg>
                                            <SvgFragment type='info'/>
                                            <InfoMenu gap='8px'>
                                                <Text $bold $lighter>Credit usage:</Text>
                                                <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
                                                    {[{value: 'Business email', credits: '(1 Credits)'}, {value: 'Personal email', credits: '(3 Credits)'}, {value: 'Phone number', credits: '(10 Credits)'}].map((item, key) => (
                                                        <div key={key} style={{display: 'flex', gap: '5px'}}>
                                                            <Text $bold>{item.value}</Text>
                                                            <Text>{item.credits}</Text>
                                                        </div>
                                                    ))}
                                                </div>
                                            </InfoMenu>
                                        </InfoMenuSvg>
                                    </EnrichmentCredits>
                                </>
                                }
                            </Column>
                    </div>
                }
                </div>
                <StyledConnectionProperties gap='16px' $inboxSurface={inboxSurface}>
                    <Button $spaceBetween $type={inboxSurface ? 'white' : 'black100'} onClickHandler={() => setConnectionProperties(!connectionProperties)}>
                        <div>More info</div>
                        <StyledArrowSvg 
                            dropdownOpen={connectionProperties} 
                            xmlns="http://www.w3.org/2000/svg" 
                            width="12" 
                            height="12" 
                            viewBox="0 0 12 7"
                        >
                            <path fillRule="evenodd" d="M6.193 14.215l.623.6c.257.246.674.247.932 0L12 10.747l4.252 4.068c.258.247.675.247.932 0l.623-.6c.257-.247.257-.648 0-.896l-5.34-5.133c-.258-.248-.676-.248-.933 0l-5.34 5.133c-.258.247-.258.649 0 .896z" transform="translate(-209 -182) translate(30 120) translate(11 54) translate(162)"/>
                        </StyledArrowSvg>
                    </Button>
                    {connectionProperties &&
                        <Column gap="16px" className="connection-properties">
                            <SimplePropertyRow label="Li Email" value={currentConnection.email} />
                            <SimplePropertyRow label="Li Phone" value={currentConnection.phoneNumber} />
                            <SimplePropertyRow label="Company" value={currentConnection.company} />
                            <SimplePropertyRow label="Location" value={currentConnection.location} />
                            <SimplePropertyRow label="Industry" value={currentConnection.industry} />
                            <SimplePropertyRow label="Date Connected" value={currentConnection.connectedAt ? moment(currentConnection.connectedAt).format('MMM D, YYYY [at] HH:mm') : undefined}/>
                            <SimplePropertyRow label="Number of followers" value={ typeof currentConnection.followerCount === 'number' ? currentConnection.followerCount.toLocaleString() : undefined}/>
                        </Column>
                    }
                </StyledConnectionProperties>
                </Column>
            <Column gap='10px'>
                <Row spaceBetween alignItems>
                    <Text $heading6>{'Tasks'}</Text>
                    {!addTask && tasks && tasks.length > 0 &&
                    <ActionText 
                        onClickHandler={() => setAddTask(true)} 
                        $disabled={addingTask}>{'Add task'}</ActionText>}
                </Row>

                {addTask && !editingTask && (
                    <ShortTaskFormSurface>
                        <CreateEditTaskForm
                                task={editingTask}
                                formType={'short'}
                                connection={currentConnection}
                                onCreateHandler={(taskId: string | null, task: any) => onCreateUpdateTaskHandler(taskId, task)}
                                onCloseHandler={() => onCloseTaskFormHandler()} 
                            />
                    </ShortTaskFormSurface>
                )} 
                {tasks && tasks.length > 0 ? (
                    tasks.map((task: any) => {
                            return editingTask && editingTask._id === task._id ? (
                                <ShortTaskFormSurface key={task._id}>
                                <CreateEditTaskForm
                                    task={editingTask}
                                    formType={'short'}
                                    connection={currentConnection}
                                    onCreateHandler={(taskId: string | null, task: any) => onCreateUpdateTaskHandler(taskId, task)}
                                    onCloseHandler={() => onCloseTaskFormHandler()} 
                                />
                                </ShortTaskFormSurface>
                            ) :  (
                                <StyledNoteRow spaceBetween $inboxSurface={inboxSurface}>
                                    <Column gap='10px'>
                                        <Text $bold $black>{parseLinksInText(task.title, styles.colors.primary600)}</Text>
                                        <Text $label $lighter>{task.end ? moment.unix(task.end).format("MMM D, YYYY") : 'No due date'}</Text>
                                    </Column>
                                    <EditDeleteActionDropdown
                                        object={task}
                                        type='task'
                                        onDeleteHandler={() => onDeleteTaskHandler(task._id)}
                                        onEditHandler={() => onEditTaskHandler(task)}
                                    />
                                </StyledNoteRow>
                            )
                    })
                ) : (
                    !editingTask && !addTask && !addingTask && (
                        <EmptyListWrapper $inboxSurface={inboxSurface}>
                            <EmptyListLabel>No tasks to show</EmptyListLabel>
                            <EmptyListButton onClick={() => setAddTask(true)}>Add task</EmptyListButton>
                        </EmptyListWrapper>
                        )   
                    )
                }
                <Row spaceBetween alignItems>
                    <Text $heading6>{'Tags'}</Text>
                </Row>
                <ApplyRemoveTagForm
                    inboxSurface={inboxSurface}
                    connection={currentConnection}
                    tags={onConversationApplyRemoveTags
                        ? (conversationTags || []).filter((t: any) => showAutoTags || !t.isForbidden).sort((a: any, b: any) => a?.title?.localeCompare(b?.title))
                        : (!currentConnection ? [] : showAutoTags ? currentConnection?.tags : currentConnection?.tags?.filter(tag => !tag.isForbidden)?.sort((a, b) => a?.title?.localeCompare(b?.title)))
                    }
                    onSaveHandler={onConversationApplyRemoveTags ? undefined : submitConnectionsTagsHandler}
                    overrideApplyRemove={onConversationApplyRemoveTags}
                    onKanbanTagChanged={onConversationApplyRemoveTags ? undefined : (hasKanbanTag, updatedConnection) => {
                        if (onChangedTagsHandler) {
                            onChangedTagsHandler(updatedConnection);
                        }
                    }}
                />
                <Column gap='10px'>
                    <CreateEditNoteForm
                        connectionId={currentConnection._id}
                        existingNotes={currentConnection.notes}
                        showEmptyPage={true}
                        addScrollOffset={true}
                        inboxSurface={inboxSurface}
                        embeddedNoteFormSurface
                        onCreatedNoteHandler={onCreatedNoteHandler}
                    />
                </Column>
            </Column>
        
    </Container>
}

const Container = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow-y: auto;
    gap: 16px;
    /* Reserve space so the scrollbar doesn't cover content */
    padding-right: 4px;
    scrollbar-gutter: stable;

    /* Subtle scrollbar, only visible on hover */
    scrollbar-width: thin;
    scrollbar-color: transparent transparent;

    &::-webkit-scrollbar { width: 4px; background: transparent; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: transparent; border-radius: 2px; }

    &:hover {
        scrollbar-color: #e8eaed transparent;
    }
    &:hover::-webkit-scrollbar-thumb {
        background: #e8eaed;
    }
`

const StylesProfilePhoto = styled(ProfilePhoto).attrs({ className: 'inbox-conversation-avatar' })`
    align-self: flex-start;
    flex-shrink: 0;
    border-radius: 50%;
`
const UpdateVisitProfileSection = styled(Row)`
    align-items: center;
`

const outlineActionBorder = '#DAE0E8';

const SidebarOutlineButton = styled(Button)`
    width: 92px !important;
    min-width: 92px;
    max-width: 92px;
    background-color: #ffffff !important;
    height: 35px !important;
    min-height: 35px;
    border-radius: 4px !important;
    padding: 7px 8px !important;
    border: 1px solid ${outlineActionBorder} !important;
    gap: 10px;
    font-size: 14px;
    line-height: 16px;
    white-space: nowrap;
    box-sizing: border-box;

    &:hover:not(:disabled) {
        border: 1px solid ${styles.colors.primary600} !important;
    }
`;

const MoreOutlineWrapper = styled.div`
    width: 35px;
    min-width: 35px;
    height: 35px;
    min-height: 35px;
    border-radius: 4px;
    border: 1px solid ${outlineActionBorder};
    padding: 4px;
    gap: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
    background: #ffffff;

    &:hover {
        border-color: ${styles.colors.primary600};
    }

    .dropdownWrapperReference {
        width: 100%;
        justify-content: center;
    }

    .dropdown-toggle,
    .dropdownWrapperReference {
        min-height: 20px !important;
        padding: 0 !important;
        width: 100%;
        justify-content: center;
    }
`;
const StyledNoteRow = styled(Row)<{ $inboxSurface?: boolean }>`
    width: 100%;
    padding: 10px;
    background: ${({ $inboxSurface }) => ($inboxSurface ? styles.colors.white : styles.colors.black100)};
`
const ShortTaskFormSurface = styled.div`
    background-color: ${styles.colors.white};
    border-radius: 4px;
    padding: 12px;
`
const EmptyListWrapper = styled.div<{ $inboxSurface?: boolean }>`
    height: 86px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-evenly;
    border-radius: 4px;
    background-color: ${({ $inboxSurface }) => ($inboxSurface ? styles.colors.white : styles.colors.black100)};
`
const EmptyListLabel = styled.p`
    font-weight: 700;
    line-height: 14px;
    font-size: 11px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: ${styles.colors.black300};
    font-family: NunitoSans;
`
const EmptyListButton = styled.p`
    font-family: NunitoSans;
    font-size: 14px;
    font-weight: 700;
    line-height: 20px;
    letter-spacing: 0px;
    text-align: center;
    cursor: pointer;
    padding: 8px;
    border-radius: 4px;
    color: ${({theme: {secondaryColor}}) => secondaryColor};
    background-color: ${styles.colors.primary600};
`
const StyledArrowSvg = styled.svg<{ dropdownOpen: boolean }>`
    fill: ${({dropdownOpen, theme: { quaternaryColor }}) => dropdownOpen ? quaternaryColor: styles.colors.black300 };
    transition: all .2s ease;
    cursor: pointer;
    transform: rotate(180deg);
    ${({dropdownOpen}) => dropdownOpen && 'transform: rotate(360deg);'}
    &:hover{ fill: ${props => props.theme.primaryColor }};
`
const StyledConnectionProperties = styled(Column)<{ $inboxSurface?: boolean }>`
    background-color: ${({ $inboxSurface }) => ($inboxSurface ? styles.colors.white : styles.colors.black100)};
    border-radius: 4px;

    .connection-properties {
        padding: 0px 8px 8px 8px;
    }
`
const SvgContent = styled(Row)`
    display: none;
`
const StyledSvgFragment = styled(SvgFragment)`
    width: 12px;
    height: 12px;
    &:hover path {
        stroke: ${styles.colors.primary600};
    }
`

const EnrichAgainSvgFragment = styled(SvgFragment)`
    width: 12px;
    height: 12px;
    
    &:hover .path,
    &:hover path {
        fill: ${styles.colors.primary600} !important;
        stroke: initial;
    }
`
const EnrichRow = styled(Row)<{ svgContentHover?: boolean }>`
    width: 100%;

    ${({svgContentHover}) => svgContentHover && `
        cursor: pointer;

        &:hover ${SvgContent} {
            display: flex;
        }
    ` }
`
const EnrichColumn = styled(Column)`
    width: 100%;
`
const EnrichmentCredits = styled(Row)`
    background-color: ${styles.colors.white};
    padding: 5px;
    border-radius: 4px;
`
const InfoMenu = styled(Column)`
    display: none;
    position: absolute;
    padding: 8px;
    width: 200px;
    z-index: 1;
    background-color: white;
    border-radius: 4px;
    right: -25px;
    box-shadow: 0 0 12px 0 rgba(0, 0, 0, 0.1);
`
const InfoMenuSvg = styled.div`
    position: relative;
    cursor: pointer;
    
    &:hover ${InfoMenu} {
        display: flex;
    }
`
export default ConnectionSidebarFragment
